import SiteFooter from '../SiteFooter';
import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  FormatQuote as QuoteIcon,
  CalendarMonth as CalendarIcon,
  WhatsApp as WhatsAppIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  QrCode as QrCodeIcon,
  Replay as ReplayIcon,
  MarkEmailUnread as InboxIcon,
  ManageAccounts as UsersIcon,
  Campaign as BroadcastIcon,
  BarChart as StatsIcon,
  Mic as MicIcon,
  Handshake as SponsorIcon,
  Favorite as DonationIcon,
  Password as PasswordIcon,
  MenuBook as DictionaryIcon,
} from '@mui/icons-material';

import { useAuth } from '../../contexts/AuthContext';
import ChangePasswordDialog from '../ChangePasswordDialog';

const drawerWidth = 260;

const menuItems = [
  { text: 'Tableau de bord', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Dictionnaire (site public)', icon: <DictionaryIcon />, path: '/dictionnaire', superAdminOnly: true },
  { text: 'Abonnés', icon: <PeopleIcon />, path: '/subscribers' },
  { text: 'Proverbes', icon: <QuoteIcon />, path: '/proverbs' },
  { text: 'Éphémérides', icon: <CalendarIcon />, path: '/ephemerides' },
  { text: 'Messages WhatsApp', icon: <WhatsAppIcon />, path: '/whatsapp' },
  { text: 'Config WhatsApp', icon: <QrCodeIcon />, path: '/whatsapp/config' },
  { text: 'Renvoi messages', icon: <ReplayIcon />, path: '/whatsapp/resend' },
  { text: 'Messages reçus', icon: <InboxIcon />, path: '/whatsapp/inbox' },
  { text: 'Message à tous', icon: <BroadcastIcon />, path: '/whatsapp/broadcast', superAdminOnly: true },
  { text: 'Dictionnaire : corrections', icon: <DictionaryIcon />, path: '/dictionary/validation' },
  { text: 'Enregistrement audio', icon: <MicIcon />, path: '/audio' },
  { text: 'Campagne de don', icon: <DonationIcon />, path: '/donations', superAdminOnly: true },
  { text: 'Parrains', icon: <SponsorIcon />, path: '/sponsors', superAdminOnly: true },
  { text: 'Fréquentation', icon: <StatsIcon />, path: '/stats', superAdminOnly: true },
  { text: 'Utilisateurs', icon: <UsersIcon />, path: '/users', superAdminOnly: true },
  { text: 'Paramètres', icon: <SettingsIcon />, path: '/settings' },
];

const MainLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/login');
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        py: 2 
      }}>
        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 700 }}>
          📅 Éphéméride Duala
        </Typography>
        {isMobile && (
          <IconButton onClick={handleDrawerToggle} sx={{ ml: 1 }}>
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Toolbar>
      
      <Divider />
      
      <List sx={{ flexGrow: 1, px: 1 }}>
        {menuItems.filter(item => !item.superAdminOnly || (user && user.role === 'super_admin')).map((item) => {
          const isActive = location.pathname === item.path || 
                          location.pathname.startsWith(item.path + '/');
          
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                sx={{
                  borderRadius: 2,
                  backgroundColor: isActive ? 'primary.main' : 'transparent',
                  color: isActive ? 'white' : 'text.primary',
                  '&:hover': {
                    backgroundColor: isActive ? 'primary.dark' : 'action.hover',
                  },
                }}
              >
                <ListItemIcon sx={{ color: isActive ? 'white' : 'inherit', minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      
      <Divider />
      
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Version 1.0.0
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: 'white',
          color: 'text.primary',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ flexGrow: 1 }} />
          
          <Box 
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={handleMenuClick}
          >
            <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
              {user?.first_name?.[0] || 'A'}
            </Avatar>
            <Box sx={{ ml: 1, display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {user?.first_name} {user?.last_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.role === 'super_admin' ? 'Super Admin' : user?.role === 'relecteur' ? 'Relecteur' : 'Admin'}
              </Typography>
            </Box>
          </Box>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => { handleMenuClose(); navigate('/settings'); }}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              Paramètres
            </MenuItem>
            <MenuItem onClick={() => { handleMenuClose(); setPasswordDialogOpen(true); }}>
              <ListItemIcon>
                <PasswordIcon fontSize="small" />
              </ListItemIcon>
              Changer mon mot de passe
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Déconnexion
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: 'background.default',
          mt: '64px',
        }}
      >
        <Outlet />
        <SiteFooter />
      </Box>

      <ChangePasswordDialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} />
    </Box>
  );
};

export default MainLayout;
